/**
 * The Daily streak card's data (specs/home-page AC-33, AC-34): the shared economy store's claim state,
 * reduced by the same ladder derivation the Profile's Daily Rewards card uses, so the two surfaces can
 * never disagree about which day the player is on or whether today is still unclaimed.
 */

import { useMemo } from 'react';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useCountdownToDate } from '../../challenges/hooks/useCountdownToDate';
import type { CountdownSegments } from '../../challenges/helpers/countdownToDate.helper';
import { deriveDailyLadder, type DailyLadder } from '../../profile/components/DailyRewardsCard/DailyRewardsCard.helper';
import type { HomeStreakModel } from '../types/home.types';

export interface UseHomeStreakReturn {
  model: HomeStreakModel;
}

/** Shape a derived ladder (+ the countdown to the next claim) for the card. Pure; exported for tests. */
export function buildStreakModel(ladder: DailyLadder, nextIn: string | null): HomeStreakModel {
  return {
    days: ladder.collectedCount,
    canClaim: ladder.canClaim,
    todayReward: ladder.todayReward,
    nextIn: ladder.canClaim ? null : nextIn,
    weekComplete: ladder.weekComplete,
    dots: ladder.days.map((d) => ({
      day: d.day,
      state: d.claimed ? 'claimed' : d.today ? 'today' : 'locked',
    })),
  };
}

/** "9h 05m" from countdown segments; null until the countdown has a target. */
export function formatNextIn(segments: CountdownSegments | null): string | null {
  if (!segments) return null;
  return `${segments.hours}h ${String(segments.minutes).padStart(2, '0')}m`;
}

export const useHomeStreak = (): UseHomeStreakReturn => {
  const dailyClaimStreak = useEconomyStore((s) => s.dailyClaimStreak);
  const lastDailyClaimAt = useEconomyStore((s) => s.lastDailyClaimAt);
  const nextClaimAt = useEconomyStore((s) => s.nextClaimAt);
  const { segments } = useCountdownToDate(nextClaimAt);

  const model = useMemo(
    () => buildStreakModel(deriveDailyLadder(dailyClaimStreak, lastDailyClaimAt, nextClaimAt), formatNextIn(segments)),
    [dailyClaimStreak, lastDailyClaimAt, nextClaimAt, segments],
  );

  return { model };
};
